import PetBirthdayIcon from "@/app/components/ui/Reusable/PetBirthdayIcon";
import {
  atHomeAppointmentConfig,
  atHomeStatusToActionConfigKey,
  type AtHomeAppointmentActionConfigItem,
} from "../constants";
import toast from "react-hot-toast";
import { ROUTES } from "@/app/routes";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useUtils } from "@/app/hooks/useUtils";
import RupeeImage from "@assets/images/rupee.svg";
import { useLoader } from "@/app/hooks/useLoader";
import HighRiskIcon from "@/app/components/ui/Reusable/HighRiskIcon";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import RBACFlag from "@/app/components/core/RBACFlag";
import { Dialog, Transition } from "@headlessui/react";
import Breed from "@/app/components/ui/Reusable/Breed";
import { usePWLEffect } from "@/app/hooks/usePWLEffect";
import AtHomeServicesStats from "../AtHomeServicesStats";
import Species from "@/app/components/ui/Reusable/Species";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import AtHomeServicesFilters from "../AtHomeServicesFilters";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import ButtonCore from "@/app/components/ui/Buttons/ButtonCore";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import ThreeDotModal from "../../PatientWaitingList/PWLTable/ThreeDotModal";
import type { IPatientWaitingListData } from "@/api_framework/api_modals/Patients";
import useRefetchAtHomeStats from "@/app/hooks/api_hooks/process/useReFetchAtHomeStats";
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import useRefetchAtHomeListing from "@/app/hooks/api_hooks/process/useReFetchAtHomeListing";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { useAtHomeBookingStatusUpdateAPI } from "@/app/hooks/api_hooks/AtHomeServices/useAtHomeBookingStatusUpdateAPI";

const normalizeBookingStatus = (status?: string | null) => {
  const normalizedStatus = String(status ?? "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");
  return normalizedStatus === "doctorintransit" ? "intransit" : normalizedStatus;
};

const otpRequiredAtHomeNextStatuses = new Set(["attending", "check_out"]);
const pendingAllocationStatuses = new Set(["SOFT_ALLOCATED", "UNALLOCATED"]);

// A cancelled booking no longer occupies the doctor's slot, so it never clashes.
const clashExemptBookingStatuses = new Set(["cancelled"]);

// Two bookings clash when the same staff member is booked within an hour of each other.
const CLASH_WINDOW_IN_MS = 60 * 60 * 1000;

const getBookingStartTimestamp = (bookingData: IPatientWaitingListData) => {
  if (!bookingData?.booking_start_date_time) return null;
  const startTimestamp = new Date(bookingData.booking_start_date_time).getTime();
  return Number.isNaN(startTimestamp) ? null : startTimestamp;
};

const isClashEligibleBooking = (bookingData: IPatientWaitingListData) =>
  Boolean(bookingData?.staff_id) &&
  getBookingStartTimestamp(bookingData) !== null &&
  !clashExemptBookingStatuses.has(normalizeBookingStatus(bookingData?.booking_status));

type PendingOtpUpdate = {
  bookingData: IPatientWaitingListData;
  bookingActionConfig: AtHomeAppointmentActionConfigItem;
};

type PendingConfirmUpdate = {
  bookingData: IPatientWaitingListData;
  bookingActionConfig: AtHomeAppointmentActionConfigItem;
};

const AtHomeServicesTable = () => {
  const { NO_PWL_RESULTS: noResultsImage } = useImageConstants();
  const { convertTime12HrFormat, getDisplayStaffName } = useUtils();
  const routeId = ROUTES.ROOT.children.PATIENT_WAITING_LIST.id;
  const isAtHomeListLoading = useLoader("isAtHomeServicesListingInProgress");
  const { routeState } = useRouteInfo(routeId);
  const { waitingList } = routeState.state as IPatientWaitingListState;
  const hasAnyListData = Array.isArray(waitingList) && waitingList.length > 0;
  const refetchAtHomeListing = useRefetchAtHomeListing();
  const refetchAtHomeStats = useRefetchAtHomeStats();
  const { execute: processBookingStatusUpdate } = useAtHomeBookingStatusUpdateAPI();
  const { setVisible: setAtHomeActionConfirmVisible, setInvisible: setAtHomeActionConfirmInvisible } =
    useModalActions("AT_HOME_ACTION_CONFIRM_MODAL");
  const { setVisible: setPaymentVisible, setModalData: setPaymentModalData } =
    useModalActions("BOOKINGS_PAYMENT_STATUS");
  const [stat, setStat] = useState<string>("Total");
  const { statusChipPWL: getStatusChipClassName } = useUtils();
  const [hasRequestedListAtLeastOnce, setHasRequestedListAtLeastOnce] = useState<boolean>(false);
  const [updatingBookingId, setUpdatingBookingId] = useState<string | null>(null);
  const [pendingOtpUpdate, setPendingOtpUpdate] = useState<PendingOtpUpdate | null>(null);
  const [pendingConfirmUpdate, setPendingConfirmUpdate] = useState<PendingConfirmUpdate | null>(null);
  const [otpValue, setOtpValue] = useState<string>("");

  usePWLEffect({
    withCacheActions: true,
  });

  useEffect(() => {
    if (isAtHomeListLoading || hasAnyListData) {
      setHasRequestedListAtLeastOnce(true);
    }
  }, [isAtHomeListLoading, hasAnyListData]);

  const getActionConfigByBookingStatus = (status?: string | null): AtHomeAppointmentActionConfigItem | undefined => {
    const statusKey = String(status ?? "").trim();
    const actionConfigKey = atHomeStatusToActionConfigKey[statusKey] ?? statusKey;
    return atHomeAppointmentConfig[actionConfigKey];
  };

  const openDirectionsForBooking = useCallback((bookingData: IPatientWaitingListData) => {
    const appointmentLocation = bookingData?.user_details?.custom_address?.trim();
    if (!appointmentLocation) {
      toast.error("Customer location is not available.");
      return;
    }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appointmentLocation)}`, "_blank");
  }, []);

  const refetchAtHomeBookingsAndStats = useCallback(async () => {
    await Promise.all([refetchAtHomeListing(), refetchAtHomeStats()]);
  }, [refetchAtHomeListing, refetchAtHomeStats]);

  const closeOtpModal = useCallback(() => {
    if (updatingBookingId) return;
    setPendingOtpUpdate(null);
    setOtpValue("");
  }, [updatingBookingId]);

  const closeConfirmModal = useCallback(() => {
    setPendingConfirmUpdate(null);
    setAtHomeActionConfirmInvisible();
  }, [setAtHomeActionConfirmInvisible]);

  const executeBookingStatusUpdate = useCallback(
    async (
      bookingData: IPatientWaitingListData,
      bookingActionConfig: AtHomeAppointmentActionConfigItem,
      otp?: string,
    ) => {
      setUpdatingBookingId(bookingData.booking_id);
      try {
        const didUpdate = await processBookingStatusUpdate(bookingData.booking_id, bookingActionConfig.nextStatus, otp);
        if (!didUpdate) return false;
        await refetchAtHomeBookingsAndStats();
        return true;
      } finally {
        setUpdatingBookingId(null);
      }
    },
    [openDirectionsForBooking, processBookingStatusUpdate, refetchAtHomeBookingsAndStats],
  );

  const handleOtpSubmit = useCallback(async () => {
    if (!pendingOtpUpdate) return;
    const trimmedOtp = otpValue.trim();
    if (!trimmedOtp) {
      toast.error("Please enter OTP.");
      return;
    }
    const didUpdate = await executeBookingStatusUpdate(
      pendingOtpUpdate.bookingData,
      pendingOtpUpdate.bookingActionConfig,
      trimmedOtp,
    );
    if (didUpdate) {
      setPendingOtpUpdate(null);
      setOtpValue("");
    }
  }, [executeBookingStatusUpdate, otpValue, pendingOtpUpdate]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!pendingConfirmUpdate) return;
    const didUpdate = await executeBookingStatusUpdate(
      pendingConfirmUpdate.bookingData,
      pendingConfirmUpdate.bookingActionConfig,
    );
    if (didUpdate) {
      setPendingConfirmUpdate(null);
      setAtHomeActionConfirmInvisible();
    }
  }, [executeBookingStatusUpdate, pendingConfirmUpdate, setAtHomeActionConfirmInvisible]);

  const handleUpdateBookingStatus = async (bookingData: IPatientWaitingListData) => {
    const bookingActionConfig = getActionConfigByBookingStatus(bookingData?.booking_status);
    if (!bookingActionConfig?.label || !bookingActionConfig?.nextStatus) return;
    if (!bookingData?.booking_id) {
      toast.error("Booking details are missing.");
      return;
    }
    if (otpRequiredAtHomeNextStatuses.has(String(bookingActionConfig.nextStatus).toLowerCase())) {
      setPendingOtpUpdate({ bookingData, bookingActionConfig });
      setOtpValue("");
      return;
    }
    setPendingConfirmUpdate({ bookingData, bookingActionConfig });
    setAtHomeActionConfirmVisible();
  };

  const columns: TableColumn<IPatientWaitingListData>[] = [
    {
      id: "time",
      width: 80,
      header: "Time",
      className: "text-[0.75rem] font-semibold text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{convertTime12HrFormat(row?.booking_start_time)}</span>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex">
          <span className={getStatusChipClassName(row?.booking_status)}>{row?.booking_status}</span>
        </div>
      ),
    },
    {
      id: "client",
      header: "Client",
      className: "text-[0.75rem] font-normal ",
      headerClassName: "font-semibold",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="flex items-start gap-0 min-w-0">
            <Link
              to={`/app/customer/profile/customer-details/appointments/${row?.user_id}/${row?.pet_id}`}
              className="font-semibold text-black whitespace-normal break-words min-w-0 flex-1 cursor-pointer hover:underline"
            >
              {row?.user_details?.name ?? "-"}
            </Link>
          </div>
          <div className="flex items-center gap-0 text-[#1B68D3] font-semibold text-[0.75rem]">
            {row?.is_member ? (
              <span className="inline-flex items-center gap-1">
                <span>{row?.is_member}</span>
              </span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: "client_details",
      header: "Client Details",
      className: "text-[0.75rem] font-normal text-black whitespace-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-semibold whitespace-normal break-words line-clamp-2">
            {row?.user_details?.custom_address ?? "-"}
          </span>
        </div>
      ),
    },
    {
      id: "pet_details",
      header: "Pet Details",
      className: "text-[0.75rem] font-normal text-black whitespace-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => {
        const isHighRisk = row?.pet_details?.high_risk;
        return (
          <div className="flex flex-col">
            {row?.pet_details?.has_birthday && (
              <div className="mb-1">
                <PetBirthdayIcon
                  hasBirthday={row?.pet_details?.has_birthday}
                  petName={row?.pet_details?.name}
                  className="!ml-0"
                />
              </div>
            )}
            {isHighRisk && (
              <div className="mb-1">
                <HighRiskIcon isHighRisk={isHighRisk} className="!ml-0" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="font-semibold">{row?.pet_details?.name ?? "-"}</span>
            </div>
            <span className="text-[#727272] font-semibold text-[0.75rem]">
              <Species className="" speciesId={row?.pet_details?.pet_species_type_id} />
              <span> - </span>
              <Breed
                className="text-[#727272] font-semibold text-[0.75rem] whitespace-normal break-words"
                breedId={row?.pet_details?.pet_breed_id}
                speciesId={row?.pet_details?.pet_species_type_id}
                petBreed={row?.pet_details?.pet_breed}
              />
            </span>
          </div>
        );
      },
    },
    {
      id: "reason",
      header: "Reason",
      width: 260,
      minWidth: 220,
      maxWidth: 320,
      className: "text-[0.75rem] font-normal text-black whitespace-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => <span className="text-[12px] font-semibold items-center">{row?.reason}</span>,
    },
    {
      id: "service_staff",
      header: "Service & Staff",
      className: "text-[0.75rem] text-black whitespace-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => {
        const bookingActionConfig = getActionConfigByBookingStatus(row?.booking_status);
        return (
          <div className="flex flex-col">
            <span className="font-semibold">
              {row?.service_details?.service_type ?? row?.service_details?.name ?? "-"}
            </span>
            <span className="text-[#727272] font-semibold text-[0.75rem] whitespace-normal break-words">
              {row?.subservice_details?.name ?? "-"}
              {getDisplayStaffName(row) ? ` - ${getDisplayStaffName(row)}` : ""}
            </span>
            {bookingActionConfig?.showDirections ? (
              <button
                type="button"
                data-table-stop-row-click="true"
                className="mt-1 w-fit inline-flex items-center gap-1 text-[#1B68D3] font-semibold text-[12px] hover:underline"
                onClick={(event) => {
                  event.stopPropagation();
                  openDirectionsForBooking(row);
                }}
              >
                <PaperAirplaneIcon className="w-3.5 h-3.5 -rotate-45" />
                <span>{getDisplayStaffName(row)}</span>
              </button>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "vaccination",
      header: "Vaccination",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-center text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col">
          {row?.vaccination === "over_due" && <p className="font-semibold text-[#EF0303] text-center">Overdue</p>}
          {row?.vaccination === "upcoming" && <p className="font-semibold text-[#E0A200] text-center">Upcoming</p>}
          {row?.vaccination === "missing_data" && <p className="font-semibold text-[#000] text-center">Missing Data</p>}
          {row?.vaccination === "all_done" && <p className="font-semibold text-black text-center">Up To Date</p>}
          {!row?.vaccination && <span className="text-[#808DA0] text-center">-</span>}
        </div>
      ),
    },
    {
      id: "action",
      header: "Action",
      overflow: "visible",
      className: "text-[0.75rem] font-normal text-black whitespace-nowrap",
      headerClassName: "text-[0.875rem] text-center font-semibold text-[#193A6A]",
      cell: ({ row }) => {
        const bookingActionConfig = getActionConfigByBookingStatus(row?.booking_status);
        const shouldShowActionButton = Boolean(bookingActionConfig?.label && bookingActionConfig?.nextStatus);
        const isCurrentBookingUpdating = updatingBookingId === row?.booking_id;
        return (
          <div className="flex items-center w-full gap-[10px] pl-0" data-table-stop-row-click="true">
            {shouldShowActionButton && bookingActionConfig ? (
              <RBACFlag actionId="ACTION_BUTTON_AT_HOME_SERVICES">
                <ButtonCore
                  size="PWL"
                  type={bookingActionConfig.type}
                  disabled={isCurrentBookingUpdating}
                  onButtonClick={() => handleUpdateBookingStatus(row)}
                >
                  {bookingActionConfig.label}
                </ButtonCore>
              </RBACFlag>
            ) : (
              <p className="w-20"></p>
            )}
            <div className="ml-auto" data-table-stop-row-click="true">
              <ThreeDotModal bookingDetails={row} variant="AT_HOME" />
            </div>
          </div>
        );
      },
    },
  ];

  const conflictingBookingIds = useMemo(() => {
    // Bucket every clash-eligible booking by staff, then sort each staff's bookings by
    // start time. Once sorted, only neighbours need comparing: if any two bookings fall
    // within the window, some adjacent pair does too.
    const bookingsByStaffId = new Map<string, { bookingId: string; startTimestamp: number }[]>();
    (Array.isArray(waitingList) ? waitingList : []).forEach((wl) => {
      if (!isClashEligibleBooking(wl)) return;
      const staffBookings = bookingsByStaffId.get(wl.staff_id as string) ?? [];
      staffBookings.push({ bookingId: wl.booking_id, startTimestamp: getBookingStartTimestamp(wl) as number });
      bookingsByStaffId.set(wl.staff_id as string, staffBookings);
    });

    const clashingBookingIds = new Set<string>();
    bookingsByStaffId.forEach((staffBookings) => {
      staffBookings.sort((a, b) => a.startTimestamp - b.startTimestamp);
      staffBookings.forEach((booking, index) => {
        const previousBooking = staffBookings[index - 1];
        if (!previousBooking) return;
        if (booking.startTimestamp - previousBooking.startTimestamp > CLASH_WINDOW_IN_MS) return;
        clashingBookingIds.add(previousBooking.bookingId);
        clashingBookingIds.add(booking.bookingId);
      });
    });
    return clashingBookingIds;
  }, [waitingList]);

  const getRowBgClassName = (wl: IPatientWaitingListData) => {
    if (pendingAllocationStatuses.has(wl?.allocation_status ?? "")) return "!bg-[#FFF8DC]";
    if (conflictingBookingIds.has(wl?.booking_id)) return "!bg-[#FFEBEB]";
    if (wl?.booking_status?.toLowerCase() === "completed" && wl?.invoice_details === null) return "!bg-[#FFEBEB]";
    return "bg-white";
  };

  const filteredWaitingList = useMemo(() => {
    if (!Array.isArray(waitingList)) return [];
    if (normalizeBookingStatus(stat) === "total") return waitingList;
    const selectedStat = normalizeBookingStatus(stat);
    return waitingList.filter((wl) => normalizeBookingStatus(wl?.booking_status) === selectedStat);
  }, [waitingList, stat]);

  const hasListData = filteredWaitingList.length > 0;
  const shouldShowTableLoader = isAtHomeListLoading || (!hasRequestedListAtLeastOnce && !hasListData);
  const isOtpBookingUpdating =
    Boolean(pendingOtpUpdate?.bookingData?.booking_id) &&
    updatingBookingId === pendingOtpUpdate?.bookingData?.booking_id;
  const isConfirmBookingUpdating =
    Boolean(pendingConfirmUpdate?.bookingData?.booking_id) &&
    updatingBookingId === pendingConfirmUpdate?.bookingData?.booking_id;

  return (
    <>
      <div className="bg-white">
        <div className="relative px-2">
          <h3 className="text-[1rem] lg:text-[1.125rem] text-black font-semibold mb-2 sm:mb-2 2xl:mb-4">
            At Home Appointments
          </h3>
          <AtHomeServicesStats currentStat={stat} setStatCard={setStat} />
          <AtHomeServicesFilters currentStat={stat} setStatCard={setStat} />
        </div>
        {!shouldShowTableLoader ? (
          hasListData ? (
            <div className="relative mt-6 ml-[10px]">
              <TableComponent
                columns={columns}
                data={filteredWaitingList}
                getRowId={(wl) => `${wl?.user_id}-${wl?.pet_id}-${wl?.booking_id}`}
                rowClassName={(wl, _rowIndex, ctx) => {
                  const base = `cursor-pointer ${getRowBgClassName(wl)}`;
                  if (!ctx?.canExpand) return base;
                  return ctx.isExpanded ? "cursor-pointer !bg-[#EEF5FF]" : base;
                }}
                containerClassName=""
                scrollContainerClassName="mt-0 max-h-[calc(100vh-260px)] overflow-y-auto overflow-x-auto"
                minTableWidthClassName="min-w-full"
                tableClassName="rounded-[20px]"
                bodyClassName="text-xs"
                expandable={{
                  expandOnRowClick: true,
                  showToggleInFirstColumn: false,
                  canExpandRow: () => true,
                  expandedRowClassName: "bg-[#F7FAFF]",
                  renderExpandedRowCells: (wl) => {
                    const hasPaid = wl?.booking_fee_amount != null && wl?.booking_fee_amount > 0;
                    const expandedMetaItemClass = "max-w-[200px] flex gap-1";
                    const createdByNode = (
                      <div className="max-w-[230px] flex gap-1">
                        <span className="text-[#727272] text-[0.75rem] font-semibold whitespace-nowrap">
                          Created By
                        </span>
                        <span className="font-semibold text-black whitespace-normal break-words">{wl?.created_by}</span>
                      </div>
                    );
                    const userNode = (
                      <div className={`${expandedMetaItemClass} justify-end`}>
                        <span className="text-[#727272] text-[0.75rem] font-semibold">User</span>
                        <span className="font-semibold text-black">{wl?.user_details?.customer_type || "NA"}</span>
                      </div>
                    );
                    const appNode = (
                      <div className={`${expandedMetaItemClass} justify-center`}>
                        <span className="text-[#727272] text-[0.75rem] font-semibold">App</span>
                        <span className="font-semibold text-black">{wl?.isappdownloaded ? "Yes" : "No"}</span>
                      </div>
                    );
                    const prepaidNode = (
                      <div className={expandedMetaItemClass}>
                        {hasPaid && (
                          <img src={RupeeImage} alt="" aria-hidden="true" className="w-4 h-4 shrink-0 leading-none" />
                        )}
                        <span className="text-[#727272] text-[0.75rem] font-semibold">Prepaid</span>
                        <span className="font-semibold text-black">{hasPaid ? "Yes" : "No"}</span>
                        {hasPaid && (
                          <a
                            href="#"
                            className="underline text-xs text-black ml-1 whitespace-nowrap"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPaymentModalData({
                                bookingDetails: {
                                  booking_id: wl?.booking_id,
                                },
                              });
                              setPaymentVisible();
                            }}
                          >
                            Show Payment History
                          </a>
                        )}
                      </div>
                    );
                    const invoiceNode = (
                      <div className={`${expandedMetaItemClass} justify-center`}>
                        <span className="text-[#727272] text-[0.75rem] font-semibold">Invoice</span>
                        {wl?.invoice_details?.length ? (
                          <span className="font-semibold text-black flex flex-col gap-1">
                            {wl?.invoice_details?.map((invoice, i) => {
                              const fileName = invoice?.file_name || "invoice.pdf";
                              const shortName = "Invoice " + (i + 1);
                              return (
                                <a
                                  key={i}
                                  href={invoice?.invoice_link}
                                  target="_blank"
                                  className="text-[#0068FF] text-[0.75rem] hover:underline whitespace-nowrap font-semibold"
                                  title={fileName}
                                >
                                  {shortName}
                                </a>
                              );
                            })}
                          </span>
                        ) : (
                          <span className="font-semibold text-black">NA</span>
                        )}
                      </div>
                    );
                    const baseCells: ReactNode[] = [
                      prepaidNode,
                      appNode,
                      createdByNode,
                      userNode,
                      invoiceNode,
                    ];
                    return [...baseCells];
                  },
                }}
              />
              <Tooltip id="user_detail_name" place="bottom" />
              <Tooltip id="service_detail_name" place="bottom" />
              <Tooltip id="staff_detail_name" place="bottom" />
              <Tooltip id="created_by" place="bottom" />
              <Tooltip id="comment_details2" place="bottom" />
              <Tooltip id="clinic_details_dispclay_name" place="bottom" />
            </div>
          ) : (
            <NoRecordFoundComponent
              imageURL={noResultsImage}
              message="No Appoinments Found"
              description="No appointments found for the given filters, try something else"
            />
          )
        ) : (
          <StructuredTableSkeleton columns={columns.length} className="mt-6 ml-[10px]" />
        )}
      </div>
      <Transition.Root show={Boolean(pendingOtpUpdate)} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={closeOtpModal}>
          <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block w-full max-w-[96vw] transform overflow-hidden rounded-[20px] bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-[30rem] sm:align-middle">
                <div className="rounded-[20px]">
                  <div className="rounded-t-[20px] bg-[#EEEEEE] px-4 py-3">
                    <p className="text-sm 2xl:text-[1.125rem]">
                      Enter OTP to <span className="font-semibold">{pendingOtpUpdate?.bookingActionConfig?.label}</span>
                    </p>
                  </div>
                  <div className="p-4">
                    <p className="pb-2 text-xs font-medium text-[#6B7280]">OTP</p>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otpValue}
                      onChange={(event) => setOtpValue(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="Enter OTP"
                      className="w-full rounded-[10px] border border-[#D1D5DB] px-3 py-2 text-sm text-black outline-none focus:border-[#1B68D3]"
                    />
                    <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={closeOtpModal}
                        disabled={isOtpBookingUpdating}
                        className="w-full rounded-[10px] border bg-[#E2E2E2] px-6 py-2 text-xs font-semibold text-black sm:w-auto 2xl:text-base"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleOtpSubmit}
                        disabled={isOtpBookingUpdating}
                        className={`w-full rounded-[10px] border px-6 py-2 text-xs font-semibold sm:w-auto 2xl:text-base ${
                          isOtpBookingUpdating ? "cursor-not-allowed bg-gray-300 text-gray-700" : "bg-black text-white"
                        }`}
                      >
                        {isOtpBookingUpdating ? "Updating..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <ModalPrompt
        modalId="AT_HOME_ACTION_CONFIRM_MODAL"
        showIcon={false}
        title="Are you sure?"
        dialogClassName="sm:my-8 sm:w-full sm:max-w-md sm:p-6 w-full"
        content={<p className="text-sm text-[#6B7280]">Please confirm to continue this action.</p>}
        button={{
          primaryButtonText: "Yes",
          secondaryButtonText: "Cancel",
          primaryButtonDisabled: isConfirmBookingUpdating,
          secondaryButtonDisabled: isConfirmBookingUpdating,
          primaryCallback: handleConfirmSubmit,
          secondaryCallback: closeConfirmModal,
          onCrossClick: closeConfirmModal,
          shouldHideOnClick: false,
          primaryButtonClassname:
            "inline-flex w-full justify-center rounded-md bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black sm:ml-3 sm:w-auto disabled:bg-gray-400",
          secondaryButtonClassname:
            "mt-3 flex items-center w-full justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto",
        }}
      />
    </>
  );
};

export default AtHomeServicesTable;
